import type {
  AnswerValidation,
  Survey,
  SurveyAnswers,
  SurveyAnswerValidation,
  SurveyField,
  SurveyFields,
  SurveyKey,
  SurveyTheme,
  SurveyThemeFields,
  ThemeAnswerValidation
} from '~/types/surveys'
import { cloneDeep, pick } from 'es-toolkit'
import { fromPairs } from 'es-toolkit/compat'
import { asyncFilter } from '~/utils/helpers'
import { useSurveyStore } from '~/stores/survey'
import { surveyQuestionToField, surveyQuestionToValidation } from '~/utils/survey'
import { useWebHaptics } from 'web-haptics/vue'

export const surveyMap: Record<SurveyKey, Promise<{ default: Survey }>> = {
  'survey-january-2024': import('~/assets/survey/survey-january-2024'),
  'survey-february-2024-cook': import('~/assets/survey/survey-february-2024-cook')
} as const

export function useSurvey(key: SurveyKey) {
  const surveyStore = useSurveyStore()
  const { trigger } = useWebHaptics()

  if (!surveyStore.surveys[key]) {
    surveyStore.surveys[key] = {
      themes: {},
      key: key,
      index: 0
    }
  }

  const survey = ref<Survey>()

  const isLoading = ref(false)
  const hasLoaded = ref(false)

  const validation = ref<SurveyAnswerValidation>()
  const fields = ref<SurveyFields>()
  const errors = ref<string[]>([])

  const themeIndex = computed({
    get() {
      return surveyStore.surveys[key]!.index
    },
    set(newIndex: number) {
      surveyStore.surveys[key]!.index = newIndex
    }
  })

  const filteredSurveyThemes = computed<Survey | undefined>(() => {
    if (!survey.value) {
      return undefined
    }

    return {
      ...survey.value,
      themes: survey.value.themes.filter(
        (theme) => !('visibleIf' in theme) || toValue(theme.visibleIf)
      )
    }
  })

  const currentThemeKey = computed(
    () => filteredSurveyThemes.value?.themes[themeIndex.value].key
  )
  const surveyLength = computed(() => filteredSurveyThemes.value?.themes.length ?? 0)

  const currentTheme = computed<SurveyTheme | undefined>(() => {
    return filteredSurveyThemes.value?.themes[themeIndex.value]
  })

  const answers = computed<SurveyAnswers | undefined>({
    get(): SurveyAnswers | undefined {
      return surveyStore.surveys[key]?.themes[currentThemeKey.value ?? '']?.answers
    },
    set(values: SurveyAnswers | undefined) {
      if (!values || !surveyStore.surveys[key] || !currentThemeKey.value) {
        return
      }

      const currentFieldKeys = currentFields.value.fields.map((field) => field.key)

      const clonedValues = pick(cloneDeep(values), currentFieldKeys)

      surveyStore.surveys[key].themes[currentThemeKey.value] = {
        key: currentThemeKey.value,
        answers: clonedValues
      }
    }
  })

  const loadSurvey = async () => {
    isLoading.value = true
    await surveyStore.fetch(key)
    survey.value = (await surveyMap[key]).default
    isLoading.value = false
  }

  const compileValidation = () => {
    if (!filteredSurveyThemes.value) {
      return
    }

    const result = filteredSurveyThemes.value.themes.map<ThemeAnswerValidation>(
      (theme) => {
        const answers = fromPairs(
          theme.questions.map((question) => {
            const validation = surveyQuestionToValidation(question)
            return [question.key, validation]
          })
        ) as AnswerValidation

        return {
          key: theme.key,
          answers: answers
        }
      }
    )

    validation.value = {
      themes: result
    }
  }

  const compileFields = () => {
    if (!filteredSurveyThemes.value) {
      return
    }

    const result = filteredSurveyThemes.value.themes.map<SurveyThemeFields>((theme) => {
      const fields = theme.questions.map((question) => {
        return surveyQuestionToField(question)
      })

      return {
        key: theme.key,
        fields: fields
      }
    })

    fields.value = {
      themes: result
    }
  }
  void loadSurvey().then(() => {
    compileValidation()
    compileFields()
    hasLoaded.value = true
  })

  const currentValidation = computed(() => {
    if (!validation.value) {
      return undefined
    }

    const currentTheme = validation.value.themes[themeIndex.value]
    return currentTheme.answers
  })

  const currentFields = computed<SurveyThemeFields>(() => {
    if (!fields.value) {
      return {
        key: 'not-loaded',
        fields: []
      } satisfies SurveyThemeFields
    }

    return fields.value.themes[themeIndex.value]
  })

  const checkFields = async (values: SurveyAnswers): Promise<string[]> => {
    return await asyncFilter(Object.keys(currentValidation.value!), async (key) => {
      const validation = currentValidation.value![key]
      const value = values[key]

      const currentField = currentFields.value.fields.find((field) => field.key === key)
      if (currentField?.visibleIf && !currentField.visibleIf(values)) {
        return false
      }

      if (currentField?.type === 'description') {
        return false
      }

      const { success: valid } = await validation.safeParseAsync(value)

      return !valid
    })
  }

  const canAddStep = computed(() => themeIndex.value < surveyLength.value - 2)
  const nextStep = async (
    values: SurveyAnswers | undefined,
    isFinished: boolean
  ): Promise<boolean> => {
    if (!currentValidation.value || !values) {
      return false
    }

    const answers = cloneDeep(values)
    const themeKey = currentThemeKey.value

    const invalidKeys = await checkFields(answers)
    const isValid = invalidKeys.length === 0
    errors.value = invalidKeys

    void trigger(isValid ? 'success' : 'error')

    if (isValid) {
      themeIndex.value += 1
      storeSurveyStep(answers, themeKey, isFinished)
    }
    return isValid
  }

  const previousStep = async (values: SurveyAnswers | undefined): Promise<boolean> => {
    if (!currentValidation.value || !values) {
      return false
    }

    if (themeIndex.value <= 0) {
      return false
    }

    const answers = cloneDeep(values)
    const themeKey = currentThemeKey.value

    const invalidKeys = await checkFields(answers)
    const isValid = invalidKeys.length === 0
    themeIndex.value -= 1

    if (isValid) {
      storeSurveyStep(answers, themeKey, false)
    }
    return true
  }
  const canSubtractStep = computed(
    () => themeIndex.value > 0 && currentThemeKey?.value !== 'outro'
  )

  const isFinished = computed(() => currentThemeKey.value === 'outro')

  const storeSurveyStep = (
    values: SurveyAnswers,
    themeKey: string | undefined,
    isFinished: boolean
  ) => {
    if (!themeKey || themeKey === 'introduction') {
      return
    }

    const currentFieldKeys = filteredSurveyThemes.value?.themes
      .find((theme) => theme.key === themeKey)
      ?.questions.map((question) => question.key)

    if (!currentFieldKeys) {
      return
    }

    const clonedValues = pick(values, currentFieldKeys)

    surveyStore.surveys[key]!.themes[themeKey] = {
      key: themeKey,
      answers: clonedValues
    }

    void surveyStore.update(key, isFinished)
  }

  const getFilteredFields = (
    valuesRef: MaybeRefOrGetter<SurveyAnswers | undefined>
  ): SurveyField[] => {
    const values = toValue(valuesRef)

    return currentFields.value.fields.filter((field) => {
      if (!field.visibleIf) {
        return true
      } else if (!values) {
        return false
      }

      return field.visibleIf(values)
    })
  }

  return {
    isLoading,
    hasLoaded,
    themeIndex,
    surveyLength,
    currentTheme,
    currentValidation,
    currentFields,
    getFilteredFields,
    answers,
    errors,
    nextStep,
    canAddStep,
    previousStep,
    canSubtractStep,
    isFinished
  }
}
